/**
 * Layer definitions
 *
 * Shared metadata for the layer panels' checkbox groups. Lives at the layers
 * feature root so the single `LayersPanel` (authenticated + public read-only
 * modes) consumes one source of truth (no per-mode copies).
 *
 * @module components/layers/layerDefinitions
 */
import { isArtifactEnabled } from '@/config/features';
import { STANDARD_LAYER_DEFINITIONS } from '@/config/mapFeatures';
import type { ArtifactVisibility, LayerVisibility } from '@/types/layers';

/** A single toggleable layer row: store key + its i18n label key. */
export interface LayerDefinition<TKey extends string> {
  key: TKey;
  labelKey: string;
}

/** Artifact (tasks / notes / obstructions) layer rows. */
export const ARTIFACT_LAYERS: LayerDefinition<keyof ArtifactVisibility>[] = [
  { key: 'tasks', labelKey: 'layers.artifacts.tasks' },
  { key: 'notes', labelKey: 'layers.artifacts.notes' },
  { key: 'obstructions', labelKey: 'layers.artifacts.obstructions' },
];

/** Artifact rows whose feature flag is enabled (the only ones ever rendered). */
export const VISIBLE_ARTIFACT_LAYERS = ARTIFACT_LAYERS.filter((layer) =>
  isArtifactEnabled(layer.key)
);

/**
 * Standard layer rows, derived from the single-source registry
 * ({@link STANDARD_LAYER_DEFINITIONS}). The panel renders all of them; country
 * scoping is applied by the consumer via `layersAllowedForCountry`.
 */
export const STANDARD_LAYERS: LayerDefinition<keyof LayerVisibility>[] =
  STANDARD_LAYER_DEFINITIONS.map((definition) => ({
    key: definition.key,
    labelKey: definition.labelKey,
  }));
